/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';

import { updateMeta } from 'Store/Meta/Meta.action';
import { changeNavigationState } from 'Store/Navigation/Navigation.action';
import { TOP_NAVIGATION_TYPE } from 'Store/Navigation/Navigation.reducer';
import { getQueryParam } from 'Util/Url';

import { CategoriesDispatcher } from '../../store/Categories';
import { PostsDispatcher } from '../../store/Posts';
import CategoryListing from './CategoryListing.component';

export const BreadcrumbsDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/Breadcrumbs/Breadcrumbs.dispatcher'
);

/** @namespace ScandiPWA/Blog/Component/CategoryListing/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    posts: state.PostsReducer.posts,
    isLoaded: state.PostsReducer.isLoaded,
    categories: state.CategoriesReducer.categories,
    posts_per_page: state.ConfigReducer.blog_posts_per_page,
    mfblog_permalink_route: state.ConfigReducer.mfblog_permalink_route,
    blog_title: state.ConfigReducer.blog_title
});

/** @namespace ScandiPWA/Blog/Component/CategoryListing/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    requestPosts: (options) => PostsDispatcher.handleData(dispatch, options),
    requestCategories: (options) => CategoriesDispatcher.handleData(dispatch, options),
    updateBreadcrumbs: (breadcrumbs) => BreadcrumbsDispatcher.then(
        ({ default: dispatcher }) => dispatcher.update(breadcrumbs, dispatch)
    ),
    updateMeta: (meta) => dispatch(updateMeta(meta)),
    setHeaderState: (stateName) => dispatch(changeNavigationState(TOP_NAVIGATION_TYPE, stateName))
});

/** @namespace ScandiPWA/Blog/Component/CategoryListing/Container */
export class CategoryListingContainer extends PureComponent {
    static propTypes = {
        requestPosts: PropTypes.func.isRequired,
        requestCategories: PropTypes.func.isRequired,
        updateBreadcrumbs: PropTypes.func.isRequired,
        posts: PropTypes.shape({
            items: PropTypes.array,
            count: PropTypes.number
        }).isRequired,
        categories: PropTypes.shape({
            items: PropTypes.array,
            count: PropTypes.number
        }).isRequired,
        posts_per_page: PropTypes.number.isRequired
    };


    __construct(props) {
        super.__construct(props);

        const { posts_per_page } = this.props;

        this.state = {
            category: '',
            categoryObject: {},
            posts_per_page: posts_per_page ? posts_per_page : 10
        };
        this.options = {
            postsOptions: {
                sort: 'DESC',
                sortFiled: 'publish_time',
                pageSize: posts_per_page ? posts_per_page : 10,
                getDescription: true,
                getMedia: true
            }
        };

        this.containerFunctions = {
            requestPosts: this.requestPosts.bind(this),
            requestPage: this.requestPage.bind(this),
            requestCategories: this.requestCategories.bind(this),
            loadMore: this.loadMore.bind(this)
        };
    }

    componentDidMount() {
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 500);
        this.requestPosts({ filter: { category_id: { eq: this.getCategory() } } });
        this.requestCategories();
    }

    componentDidUpdate() {
        const { category: prevCategory, categoryObject } = this.state;
        const { categories: { items } } = this.props;
        if (prevCategory === null || (items.length && !Object.values(categoryObject).length) || this.getCategory() !== prevCategory) {
            this.updateCategory();
        }
    }

    getCategory() {
        return this.props.category_id;
    }

    updateCategory() {
        let { categories, posts_per_page } = this.props;
        const category = this.getCategory();
        let currentCategory = {};
        categories.items.map(cat => {
            if (cat.category_id === category) {
                currentCategory = cat;
                if (cat.posts_per_page) {
                    posts_per_page = cat.posts_per_page;
                    if (cat.category_id === 8)
                        posts_per_page = 300;
                }
            }
        });
        const options = category ? { filter: { category_id: { eq: category } } } : {};
        this.setState({ category, categoryObject: currentCategory, posts_per_page });

        this.requestPosts(options);
    }

    requestPosts(options, isLoadMore = false) {
        let { requestPosts, categories, posts_per_page } = this.props;
        let { postsOptions } = this.options;
        const category = this.getCategory();
        let currentCategory = {};
        if (!isLoadMore) {
            categories.items.map(cat => {
                if (cat.category_id === category) {
                    currentCategory = cat;
                    if (cat.posts_per_page) {
                        postsOptions.pageSize = cat.posts_per_page;
                    }
                }
            });
        }

        if (options && options.isSpeacialOffer) {
            const { filter } = options;
            requestPosts({ ...postsOptions, filter: filter, currentPage: options && options.currentPage ? options.currentPage : this._getPageFromUrl(), isSpeacialOffer: true });
        } else {
            requestPosts({ ...postsOptions, ...options, currentPage: options && options.currentPage ? options.currentPage : this._getPageFromUrl() });
        }
    }

    requestCategories() {
        const { requestCategories } = this.props;
        requestCategories();
    }

    _getPageFromUrl() {
        const { location } = this.props;
        return +(getQueryParam('page', location) || 1);
    }

    requestPage(requestedOptions) {
        const { category_id } = this.props;
        self = this;
        setTimeout(
            () => {
                if (requestedOptions) {
                    self.requestPosts(requestedOptions);
                } else {
                    const options = category_id ? { filter: { category_id: { eq: category_id } } } : {};
                    self.requestPosts(options);
                }
                window.scrollTo({ top: 0, behavior: 'smooth' });
            },
            1
        );
    }

    loadMore() {
        const { postsOptions, postsOptions: { pageSize } } = this.options;
        const { posts_per_page } = this.state;

        this.options = {
            postsOptions: { ...postsOptions, pageSize: pageSize + posts_per_page }
        };

        this.requestPosts({ filter: { category_id: { eq: this.getCategory() } } }, true);
    }

    render() {
        const { category, categoryObject, posts_per_page } = this.state;
        const { postsOptions: { pageSize } } = this.options;

        return (
            <CategoryListing
                {...this.props}
                {...this.containerFunctions}
                category={category}
                categoryObject={categoryObject}
                pageSize={posts_per_page}
                posts_per_page={posts_per_page}
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(CategoryListingContainer);
